import { z } from "zod";

import { api } from "@/modules/api";

import { isCommercialPlanKey } from "./catalog";
import {
  CommercialAllowanceKind,
  CommercialMeterKey,
  CommercialPlanChangeStatus,
  CommercialPlanChangeTiming,
  type CommercialCatalogSnapshot,
  type CommercialPlanChangePreview,
  type CommercialPlanChangeResult,
  type CommercialPlanKey,
} from "./types";

const COMMERCIAL_METER_KEYS = Object.values(CommercialMeterKey);

const commercialAllowanceComparisonSchema = z.object({
  current_amount: z.number().nonnegative().nullable().optional(),
  current_kind: z.enum(Object.values(CommercialAllowanceKind)),
  is_reduction: z.boolean(),
  meter_key: z.enum(COMMERCIAL_METER_KEYS),
  target_amount: z.number().nonnegative(),
});

const commercialPlanChangePreviewSchema = z.object({
  ends_trial: z.boolean().default(false),
  next_billing_at: z.string().datetime({ offset: true }).nullable().optional(),
  allowance_comparisons: z.array(commercialAllowanceComparisonSchema),
  amount_due_now_cents: z.number().int().nonnegative(),
  currency: z.string().nullable().optional(),
  current_paid_extra_usage_blocks: z.number().int().nonnegative(),
  current_plan_key: z.string().min(1),
  current_recurring_amount_cents: z.number().int().nonnegative().nullable().optional(),
  effective_at: z.string().min(1),
  is_legacy_migration: z.boolean(),
  one_way_legacy_exit: z.boolean(),
  provider_invoice_total_cents: z.number().int().nonnegative().nullable().optional(),
  required_migration_consent_version: z.string().min(1).nullable().optional(),
  target_paid_extra_usage_blocks: z.number().int().nonnegative(),
  target_plan_key: z.string(),
  target_recurring_amount_cents: z.number().int().nonnegative(),
  timing: z.enum(Object.values(CommercialPlanChangeTiming)),
});

const adaptCommercialPlanChangePreview = (
  value: z.infer<typeof commercialPlanChangePreviewSchema>
): CommercialPlanChangePreview => ({
  endsTrial: value.ends_trial,
  nextBillingAt: value.next_billing_at ?? null,
  allowanceComparisons: value.allowance_comparisons.map(comparison => ({
    currentAmount: comparison.current_amount ?? null,
    currentKind: comparison.current_kind,
    isReduction: comparison.is_reduction,
    meterKey: comparison.meter_key,
    targetAmount: comparison.target_amount,
  })),
  amountDueNowCents: value.amount_due_now_cents,
  currency: value.currency ?? null,
  currentPaidExtraUsageBlocks: value.current_paid_extra_usage_blocks,
  currentPlanKey: value.current_plan_key,
  currentRecurringAmountCents: value.current_recurring_amount_cents ?? null,
  effectiveAt: value.effective_at,
  isLegacyMigration: value.is_legacy_migration,
  oneWayLegacyExit: value.one_way_legacy_exit,
  providerInvoiceTotalCents: value.provider_invoice_total_cents ?? null,
  requiredMigrationConsentVersion: value.required_migration_consent_version ?? null,
  targetPaidExtraUsageBlocks: value.target_paid_extra_usage_blocks,
  targetPlanKey: value.target_plan_key as CommercialPlanKey,
  targetRecurringAmountCents: value.target_recurring_amount_cents,
  timing: value.timing,
});

const expectedTargetAllowance = ({
  catalog,
  meterKey,
  targetPaidExtraUsageBlocks,
  targetPlanKey,
}: {
  catalog: CommercialCatalogSnapshot;
  meterKey: CommercialMeterKey;
  targetPaidExtraUsageBlocks: number;
  targetPlanKey: CommercialPlanKey;
}): number | null => {
  const plan = catalog.plans.find(candidate => candidate.key === targetPlanKey);
  if (!plan) {
    return null;
  }

  return (
    plan.allowances[meterKey] +
    catalog.extraUsageBlock.allowances[meterKey] * targetPaidExtraUsageBlocks
  );
};

export const parseCommercialPlanChangePreview = ({
  catalog,
  expectedCurrentPaidExtraUsageBlocks,
  expectedCurrentPlanKey,
  expectedLegacyMigration,
  targetPaidExtraUsageBlocks,
  targetPlanKey,
  value,
}: {
  catalog: CommercialCatalogSnapshot;
  expectedCurrentPaidExtraUsageBlocks: number;
  expectedCurrentPlanKey: string;
  expectedLegacyMigration: boolean;
  targetPaidExtraUsageBlocks: number;
  targetPlanKey: CommercialPlanKey;
  value: unknown;
}): CommercialPlanChangePreview | null => {
  const parsed = commercialPlanChangePreviewSchema.safeParse(value);
  if (!parsed.success || !isCommercialPlanKey(parsed.data.target_plan_key)) {
    return null;
  }

  if (
    parsed.data.target_plan_key !== targetPlanKey ||
    parsed.data.current_plan_key !== expectedCurrentPlanKey ||
    parsed.data.target_paid_extra_usage_blocks !== targetPaidExtraUsageBlocks ||
    parsed.data.current_paid_extra_usage_blocks !== expectedCurrentPaidExtraUsageBlocks ||
    parsed.data.is_legacy_migration !== expectedLegacyMigration
  ) {
    return null;
  }

  if (
    parsed.data.one_way_legacy_exit !== expectedLegacyMigration ||
    (expectedLegacyMigration
      ? !parsed.data.required_migration_consent_version
      : parsed.data.required_migration_consent_version !== null &&
        parsed.data.required_migration_consent_version !== undefined)
  ) {
    return null;
  }

  const targetPlan = catalog.plans.find(candidate => candidate.key === targetPlanKey);
  if (parsed.data.ends_trial && !parsed.data.next_billing_at) {
    return null;
  }
  if (!targetPlan || (targetPaidExtraUsageBlocks > 0 && !targetPlan.supportsPaidExtraUsage)) {
    return null;
  }

  const seenMeterKeys = new Set<string>();
  for (const comparison of parsed.data.allowance_comparisons) {
    if (seenMeterKeys.has(comparison.meter_key)) {
      return null;
    }

    seenMeterKeys.add(comparison.meter_key);
    const expectedAllowance = expectedTargetAllowance({
      catalog,
      meterKey: comparison.meter_key,
      targetPaidExtraUsageBlocks,
      targetPlanKey,
    });
    if (comparison.target_amount !== expectedAllowance) {
      return null;
    }
  }

  if (seenMeterKeys.size !== COMMERCIAL_METER_KEYS.length) {
    return null;
  }

  const expectedRecurringAmount =
    targetPlan.monthlyPriceCents +
    catalog.extraUsageBlock.monthlyPriceCents * targetPaidExtraUsageBlocks;
  if (parsed.data.target_recurring_amount_cents !== expectedRecurringAmount) {
    return null;
  }

  return adaptCommercialPlanChangePreview(parsed.data);
};

interface CommercialPlanChangeRequest {
  catalog: CommercialCatalogSnapshot;
  currentPaidExtraUsageBlocks: number;
  currentPlanKey: string;
  isLegacyMigration: boolean;
  targetPaidExtraUsageBlocks: number;
  targetPlanKey: CommercialPlanKey;
}

export const previewCommercialPlanChange = async ({
  catalog,
  currentPaidExtraUsageBlocks,
  currentPlanKey,
  isLegacyMigration,
  targetPaidExtraUsageBlocks,
  targetPlanKey,
}: CommercialPlanChangeRequest): Promise<CommercialPlanChangePreview> => {
  const response = await api.post("/v2/me/subscriptions/stripe/plan-change/preview", {
    body: {
      paid_extra_usage_blocks: targetPaidExtraUsageBlocks,
      target_plan_key: targetPlanKey,
    },
  });
  const preview = parseCommercialPlanChangePreview({
    catalog,
    expectedCurrentPaidExtraUsageBlocks: currentPaidExtraUsageBlocks,
    expectedCurrentPlanKey: currentPlanKey,
    expectedLegacyMigration: isLegacyMigration,
    targetPaidExtraUsageBlocks,
    targetPlanKey,
    value: response.data,
  });
  if (!preview) {
    throw new Error("Mem could not verify this plan change. Your current plan was not changed.");
  }

  return preview;
};

export const applyCommercialPlanChange = async ({
  catalog,
  currentPaidExtraUsageBlocks,
  currentPlanKey,
  idempotencyKey,
  isLegacyMigration,
  preview,
}: {
  catalog: CommercialCatalogSnapshot;
  currentPaidExtraUsageBlocks: number;
  currentPlanKey: string;
  idempotencyKey: string;
  isLegacyMigration: boolean;
  preview: CommercialPlanChangePreview;
}): Promise<CommercialPlanChangeResult> => {
  const response = await api.post("/v2/me/subscriptions/stripe/plan-change", {
    body: {
      confirms_allowance_changes: true,
      confirms_trial_end: preview.endsTrial === true,
      confirms_one_way_legacy_exit: preview.oneWayLegacyExit,
      migration_consent_version: preview.requiredMigrationConsentVersion,
      paid_extra_usage_blocks: preview.targetPaidExtraUsageBlocks,
      target_plan_key: preview.targetPlanKey,
    },
    params: {
      header: {
        "Idempotency-Key": idempotencyKey,
      },
    },
  });
  const parsedResult = z
    .object({
      preview: commercialPlanChangePreviewSchema,
      provider_subscription_id: z.string().min(1),
      status: z.enum(Object.values(CommercialPlanChangeStatus)),
    })
    .safeParse(response.data);
  if (!parsedResult.success) {
    throw new Error(
      "Mem could not confirm this plan change. Your current plan remains authoritative."
    );
  }

  const verifiedPreview = parseCommercialPlanChangePreview({
    catalog,
    expectedCurrentPaidExtraUsageBlocks: currentPaidExtraUsageBlocks,
    expectedCurrentPlanKey: currentPlanKey,
    expectedLegacyMigration: isLegacyMigration,
    targetPaidExtraUsageBlocks: preview.targetPaidExtraUsageBlocks,
    targetPlanKey: preview.targetPlanKey,
    value: parsedResult.data.preview,
  });
  if (!verifiedPreview) {
    throw new Error("Mem could not verify the completed plan change. Refresh before trying again.");
  }

  return {
    preview: verifiedPreview,
    providerSubscriptionId: parsedResult.data.provider_subscription_id,
    status: parsedResult.data.status,
  };
};
